import { IClient } from 'services/apiGatewayClient';
import { State as GlobalState } from 'store';
import { actions, selectors, State } from './ducks';
import { EndpointCreator } from './interfaces';
import { RestApiId } from './restApis';
import {
  newCreateSaga,
  newDeleteSaga,
  newGetCollectionSaga,
  newGetSaga,
  newPatchSaga,
  newRedirectAfterCreateSaga,
  newRedirectAfterUpdateSaga,
  newUpdateSaga,
  PathCreator,
} from './sagas';
import {
  ClearWatchOptions,
  GetCollectionWatchOptions,
  GetWatchOptions,
  newClearWatchSaga,
  newGetCollectionWatchSaga,
  newGetWatchSaga,
} from './watchSagas';

type Saga =
  | 'CREATE'
  | 'DELETE'
  | 'GET'
  | 'GET_COLLECTION'
  | 'PATCH'
  | 'UPDATE'
  | 'REDIRECT_AFTER_CREATE'
  | 'REDIRECT_AFTER_UPDATE';

type SagaCreator = () => any;

export interface ServiceOptions<T, S> {
  // Unique identifier for the service we're creating - so reducer etc. can identify actions.
  service: S;

  // Client for retrieving the data
  client: IClient<T>;

  // Endpoint for all requests - this can be specified as either a string or a function.
  //
  // If using a string, parameters can be used eg. '/sites/${siteId}/campaigns'.
  // Any parameters specified will be replaced by the corresponding value from:
  //   - any parameters specified with the request
  //   - matching router parameters
  //
  // If providing a function, it should have the signature (state: State, params: {[key: string]: string}) => string.
  // This allows us to select items from the global state to use as parameters when creating the endpoint.
  //
  // In either case, the resulting string should be the endpoint to hit for POST and GET_COLLECTION
  // requests.  '/${id}' will be appended for all other requests.
  endpointCreator: EndpointCreator;

  // Function to calculate how to get from the global state, to the state of the particular
  // section we are creating.
  stateConverter: (state: GlobalState) => State<T>;

  // List of Sagas that we wish to create. Each saga will listen for the relevant request
  // action (e.g Get will listen for a RequestGet action), perform the relevant api calls
  // asynchronously and then update the state accordingly.
  // If not specified, will create all sagas except the redirects.
  sagas?: Saga[];

  // If we wish to use the redirect sagas, we need to specify the path to redirect to
  // which will likely change depending on the object. We specify a function to calculate
  // the redirect path, depending on the siteId and entityId.
  // TODO: Change this to a more general function which can take the state.
  redirectAfterCreateUrl?: PathCreator;
  redirectAfterUpdateUrl?: PathCreator;

  // If we wish to pre-load data on either sign-in, or when the location changes, we can
  // create watches to trigger the relevant sagas.
  //
  // Get watches will trigger when the user signs in or the location changes such that
  // the specified url parameter changes. The value of this url parameter will be used as
  // the id for the get request.
  //
  // GetCollection watches will trigger when the user signs in or the location changes.
  // If the parentParam is specified, it will make a request any time this changes due
  // to a location change.  If it is not specified, we will only load on sign in.
  watches?: {
    get?: GetWatchOptions;
    getCollection?: GetCollectionWatchOptions;
    clear?: ClearWatchOptions;
  };

  // To avoid users losing their new local entities/local changes to existing entities
  // over the course of a re-login event.
  persistUpdates?: boolean;

  // If we wish to use custom endpoints to query for particular requests, we can specify
  // an endpoint override here.
  endpointOverrides?: {
    getCollection?: EndpointCreator;
  };
}

// Helper function to simplify service creation.
export function createService<T, S = RestApiId>(options: ServiceOptions<T, S>) {
  const Actions = actions<T, S>(options.service);

  const getCollectionEndpointCreator =
    (options.endpointOverrides && options.endpointOverrides.getCollection) || options.endpointCreator;

  const sagaCreators: { [key in Saga]: SagaCreator } = {
    CREATE: () => newCreateSaga(options.client, options.endpointCreator, Actions, options.service),
    DELETE: () => newDeleteSaga(options.client, options.endpointCreator, Actions, options.service),
    GET: () => newGetSaga(options.client, options.endpointCreator, Actions, options.service),
    GET_COLLECTION: () => newGetCollectionSaga(options.client, getCollectionEndpointCreator, Actions, options.service),
    PATCH: () => newPatchSaga(options.client, options.endpointCreator, Actions, options.service),
    UPDATE: () => newUpdateSaga(options.client, options.endpointCreator, Actions, options.service),
    REDIRECT_AFTER_CREATE: () => newRedirectAfterCreateSaga(options.service, options.redirectAfterCreateUrl),
    REDIRECT_AFTER_UPDATE: () => newRedirectAfterUpdateSaga(options.service, options.redirectAfterUpdateUrl),
  };

  const watches = [];
  if (!!options.watches && !!options.watches.get) {
    watches.push(newGetWatchSaga(Actions.requestGet, options.watches.get));
  }
  if (!!options.watches && !!options.watches.getCollection) {
    watches.push(newGetCollectionWatchSaga(Actions.requestGetCollection, options.watches.getCollection));
  }
  if (!!options.watches && !!options.watches.clear) {
    watches.push(newClearWatchSaga(Actions.clearAll, options.watches.clear));
  }

  const sagas = options.sagas || ['CREATE', 'DELETE', 'GET', 'GET_COLLECTION', 'PATCH', 'UPDATE'];

  return {
    Actions,
    Selectors: selectors<T>(options.stateConverter),
    Sagas: [...watches, ...sagas.map(saga => sagaCreators[saga]())],
  };
}
